import { createRouter, createWebHistory } from 'vue-router';
import { createPinia, setActivePinia } from 'pinia';
import { RolesEnum } from '~/roles/types';
import { ability } from '~/common/services/AbilityClient';
import { usersCompanyHasFeature } from '~/common/composables/useHomeRoute';

setActivePinia(createPinia());

import authRoutes from './routes/auth';
import dashboardsRoutes from './routes/dashboards';
import correctiveActionsRoutes from './routes/correctiveActions';
import companiesRoutes from './routes/companies';
import documentsRoutes from './routes/documents';
import fssRoutes from './routes/fss';
import groupsRoutes from './routes/groups';
import notificationsRoutes from './routes/notifications';
import rolesRoutes from './routes/roles';
import ticketsRoutes from './routes/tickets';
import trackingRoutes from './routes/tracking';
import usersRoutes from './routes/users';
import workflowsRoutes from './routes/workflows';
import calendarRoutes from './routes/calendar';
import reportRoutes from './routes/reports';
import categoriesRoutes from './routes/categories';

const routes = [
  ...dashboardsRoutes,
  ...correctiveActionsRoutes,
  ...companiesRoutes,
  ...documentsRoutes,
  ...fssRoutes,
  ...groupsRoutes,
  ...notificationsRoutes,
  ...rolesRoutes,
  ...ticketsRoutes,
  ...trackingRoutes,
  ...usersRoutes,
  ...workflowsRoutes,
  ...calendarRoutes,
  ...reportRoutes,
  ...categoriesRoutes,

  // Always leave this as last one
  // in order to redirect to login from dashboard when user is not authenticated
  ...authRoutes,

  // Always leave this as last one,
  // but you can also remove it
  {
    path: '/:catchAll(.*)*',
    component: () => import('~/common/pages/404.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

const userStore = useUserStore();
router.beforeEach(async (to, from, next) => {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  if (token) {
    const success = await useAuthenticationService().autoLogin(token);

    if (success) {
      location.replace(to.path);
      return;
    } else {
      location.replace('/login');
      return;
    }
  }

  if (
    to.matched.some((record) => record.meta.auth) &&
    !userStore.isAuthenticated
  ) {
    next('/login');
    return;
  }

  if (
    to.matched.some((record) => record.meta.auth) &&
    userStore.isAuthenticated &&
    !userStore.roles.includes(RolesEnum.SUPER_ADMIN)
  ) {
    const routeWithPermissions = to.matched.find(
      (record) => record.meta.permissions,
    );

    const routeWithFeature = to.matched.find((record) => record.meta.feature);

    if (!routeWithPermissions && !routeWithFeature) {
      // no permissions or features are required to access this route
      next();
      return;
    }

    const canAccessRoute = (route) => {
      const permissions = route.meta?.permissions ? route.meta.permissions : [];
      const permissionPassed =
        permissions.length === 0 ||
        permissions.every((permissionName) =>
          ability.can('Read', permissionName),
        );

      const feature = route.meta?.feature;
      const requestedFeatures = Array.isArray(feature)
        ? feature
        : feature
          ? [feature]
          : [];

      if (requestedFeatures.length === 0) {
        return permissionPassed;
      } else {
        const companyHasFeature = usersCompanyHasFeature(requestedFeatures);

        return permissionPassed && companyHasFeature;
      }
    };

    const routeToCheck = routeWithPermissions || routeWithFeature;

    if (!canAccessRoute(routeToCheck)) {
      //the page requires permission/feature and user don't have access to it
      const { getHomeRoute } = useHomeRoute();
      const homeRoute = getHomeRoute();
      next(homeRoute);
      return;
    }
  }

  next();
});

export default router;
